import { t } from "i18next";
import { match } from "ts-pattern";
import z from "zod";

import { FieldBuilder, MultiPageFormBuilder } from "../../formBuilder/index.js";
import {
  longTextSchema,
  shortTextSchema,
  valueAndLabelSchemaDeprecated,
  yesNoSchema,
  type StaticOptionsList,
} from "../../types/index.js";
import { tk, type TranslationKey } from "../../types/Translation.js";

/**
 * The multi-select field accepts only a plain key per option, not a key with
 * interpolation options, so these lists are narrower than `StaticOptionsList`.
 */
type UseOfForceTypeOptions = { label: TranslationKey; value: string }[];

/**
 * Who can be recorded as having assessed capacity. The value set is shared so
 * that every workflow's data is comparable; which of them a workflow offers is
 * its own decision.
 */
export const reportingOfficerAssessorOption: StaticOptionsList[number] = {
  label: tk("mcaCapacityAssessment.reportingOfficer"),
  value: "reportingOfficer",
};

export const ambulanceServiceAssessorOption: StaticOptionsList[number] = {
  label: tk("mcaCapacityAssessment.ambulanceService"),
  value: "ambulanceService",
};

/** Only offered where the force has a triage service to refer the decision to. */
export const localTriageServiceAssessorOption: StaticOptionsList[number] = {
  label: tk("mcaCapacityAssessment.localTriageService"),
  value: "localTriageService",
};

export const otherAssessorOption: StaticOptionsList[number] = {
  label: tk("mcaCapacityAssessment.other"),
  value: "other",
};

const assessingIndividualSchema = z.enum([
  "reportingOfficer",
  "ambulanceService",
  "localTriageService",
  "other",
]);

/**
 * Whether capacity was assessed by a health service rather than by an officer.
 * The distinction matters because an officer can transfer the form to whoever is
 * making the decision, whereas answers about an assessment made by a health
 * service have to be obtained from that service.
 */
const isAssessedByHealthService = (
  assessingIndividual: z.infer<typeof assessingIndividualSchema> | undefined,
) =>
  match(assessingIndividual)
    .with("ambulanceService", "localTriageService", () => true)
    .with("reportingOfficer", "other", undefined, () => false)
    .exhaustive();

export const defaultAssessingIndividualOptions: StaticOptionsList = [
  reportingOfficerAssessorOption,
  ambulanceServiceAssessorOption,
  otherAssessorOption,
];

export const defaultUseOfForceTypeOptions: UseOfForceTypeOptions = [
  {
    value: "handcuffing",
    label: tk("mcaCapacityAssessment.useOfForceType.options.handcuffing"),
  },
  {
    value: "unarmedSkills",
    label: tk("mcaCapacityAssessment.useOfForceType.options.unarmedSkills"),
  },
  {
    value: "dogs",
    label: tk("mcaCapacityAssessment.useOfForceType.options.dogs"),
  },
  {
    value: "baton",
    label: tk("mcaCapacityAssessment.useOfForceType.options.baton"),
  },
  {
    value: "irritantSpray",
    label: tk("mcaCapacityAssessment.useOfForceType.options.irritantSpray"),
  },
  {
    value: "limbBodyRestraints",
    label: tk(
      "mcaCapacityAssessment.useOfForceType.options.limbBodyRestraints",
    ),
  },
  {
    value: "spitGuard",
    label: tk("mcaCapacityAssessment.useOfForceType.options.spitGuard"),
  },
  {
    value: "shield",
    label: tk("mcaCapacityAssessment.useOfForceType.options.shield"),
  },
  {
    value: "ced",
    label: tk("mcaCapacityAssessment.useOfForceType.options.ced"),
  },
  {
    value: "aep",
    label: tk("mcaCapacityAssessment.useOfForceType.options.aep"),
  },
  {
    value: "firearms",
    label: tk("mcaCapacityAssessment.useOfForceType.options.firearms"),
  },
  {
    value: "otherImprovised",
    label: tk("mcaCapacityAssessment.useOfForceType.options.otherImprovised"),
  },
];

/**
 * A railway force records compliant and non-compliant handcuffing separately
 * rather than together, and carries neither attenuating energy projectiles nor
 * firearms, so it is not offered them.
 *
 * Offered in the order the force itself set out these tactics, which is the
 * order the section 136 form asks them in - an officer who records both forms
 * reads one list, not two arrangements of it.
 */
export const railwayUseOfForceTypeOptions: UseOfForceTypeOptions = [
  {
    value: "baton",
    label: tk("mcaCapacityAssessment.useOfForceType.options.baton"),
  },
  {
    value: "tacticalCommunication",
    label: tk(
      "mcaCapacityAssessment.useOfForceType.options.tacticalCommunication",
    ),
  },
  {
    value: "irritantSpray",
    label: tk("mcaCapacityAssessment.useOfForceType.options.irritantSpray"),
  },
  {
    value: "dogs",
    label: tk("mcaCapacityAssessment.useOfForceType.options.dogs"),
  },
  {
    value: "dogBite",
    label: tk("mcaCapacityAssessment.useOfForceType.options.dogBite"),
  },
  {
    value: "groundRestraint",
    label: tk("mcaCapacityAssessment.useOfForceType.options.groundRestraint"),
  },
  {
    value: "handcuffingNonCompliant",
    label: tk(
      "mcaCapacityAssessment.useOfForceType.options.handcuffingNonCompliant",
    ),
  },
  {
    value: "handcuffingCompliant",
    label: tk(
      "mcaCapacityAssessment.useOfForceType.options.handcuffingCompliant",
    ),
  },
  {
    value: "limbBodyRestraints",
    label: tk(
      "mcaCapacityAssessment.useOfForceType.options.limbBodyRestraints",
    ),
  },
  {
    value: "shield",
    label: tk("mcaCapacityAssessment.useOfForceType.options.shield"),
  },
  {
    value: "spitGuard",
    label: tk("mcaCapacityAssessment.useOfForceType.options.spitGuard"),
  },
  {
    value: "ced",
    label: tk("mcaCapacityAssessment.useOfForceType.options.ced"),
  },
  {
    value: "unarmedSkills",
    label: tk("mcaCapacityAssessment.useOfForceType.options.unarmedSkills"),
  },
  {
    value: "otherImprovised",
    label: tk("mcaCapacityAssessment.useOfForceType.options.otherImprovised"),
  },
];

export const buildMcaCapacityAssessmentFormSpecification = (args?: {
  assessingIndividualOptions?: StaticOptionsList;
  useOfForceTypeOptions?: UseOfForceTypeOptions;
}) =>
  new MultiPageFormBuilder()
    .withReadonlyFieldAlwaysVisible({
      fieldName: "introduction",
      type: "banner",
      bannerType: "info",
      body: tk("mcaCapacityAssessment.introductionBanner"),
    })

    .withFieldAlwaysVisible({
      fieldName: "assessingIndividual",
      type: "radio",
      ...new FieldBuilder()
        .withSchema(assessingIndividualSchema)
        .captureValueLabel()
        .build(),
      label: tk("mcaCapacityAssessment.assessingIndividual"),
      requiredLabel: tk("mcaCapacityAssessment.assessingIndividualRequired"),
      options:
        args?.assessingIndividualOptions ?? defaultAssessingIndividualOptions,
    })

    .withReadonlyFieldConditionallyVisible({
      fieldName: "assessingIndividualDescriptionBanner",
      type: "banner",
      bannerType: "info",
      body: tk("mcaCapacityAssessment.assessingIndividualDescriptionBanner"),
      isVisible: ({ formValues }) =>
        !isAssessedByHealthService(formValues.assessingIndividual?.value),
    })

    .withReadonlyFieldConditionallyVisible({
      fieldName: "healthServiceAssessorDescriptionBanner",
      type: "banner",
      bannerType: "warning",
      body: tk("mcaCapacityAssessment.healthServiceAssessorDescriptionBanner"),
      isVisible: ({ formValues }) =>
        isAssessedByHealthService(formValues.assessingIndividual?.value),
    })

    .withFieldConditionallyVisible({
      fieldName: "ambulanceStaffName",
      type: "textbox",
      ...new FieldBuilder().withSchema(shortTextSchema).build(),
      label: tk("mcaCapacityAssessment.ambulanceStaffName"),
      requiredLabel: tk("mcaCapacityAssessment.ambulanceStaffNameRequired"),
      isVisible: ({ formValues }) =>
        formValues.assessingIndividual?.value === "ambulanceService",
    })

    .withFieldConditionallyVisible({
      fieldName: "otherAssessor",
      type: "textbox",
      ...new FieldBuilder().withSchema(shortTextSchema).build(),
      label: tk("mcaCapacityAssessment.otherAssessor"),
      requiredLabel: tk("mcaCapacityAssessment.otherAssessorRequired"),
      isVisible: ({ formValues }) =>
        formValues.assessingIndividual?.value === "other",
    })

    .withFieldAlwaysVisible({
      fieldName: "decisionUnableToMake",
      type: "textbox",
      ...new FieldBuilder().withSchema(longTextSchema).build(),
      multiline: true,
      rows: "5",
      label: tk("mcaCapacityAssessment.decisionUnableToMake"),
      requiredLabel: tk("mcaCapacityAssessment.decisionUnableToMakeRequired"),
    })

    .withReadonlyFieldAlwaysVisible({
      fieldName: "decisionUnableToMakeBanner",
      type: "banner",
      bannerType: "info",
      body: tk("mcaCapacityAssessment.decisionUnableToMakeBanner"),
    })

    .withFieldAlwaysVisible({
      fieldName: "canUnderstandInformation",
      type: "radio",
      ...new FieldBuilder().withSchema(yesNoSchema).captureValueLabel().build(),
      label: tk("mcaCapacityAssessment.canUnderstandInformation"),
      requiredLabel: tk(
        "mcaCapacityAssessment.canUnderstandInformationRequired",
      ),
      options: [
        { label: tk("common.yes"), value: "yes" },
        { label: tk("common.no"), value: "no" },
      ],
    })

    .withReadonlyFieldAlwaysVisible({
      fieldName: "canUnderstandInformationBanner",
      type: "banner",
      bannerType: "info",
      body: tk("mcaCapacityAssessment.canUnderstandInformationBanner"),
    })

    .withFieldAlwaysVisible({
      fieldName: "canUnderstandInformationReasoning",
      type: "textbox",
      ...new FieldBuilder().withSchema(longTextSchema).build(),
      label: tk("mcaCapacityAssessment.canUnderstandInformationReasoning"),
      requiredLabel: tk(
        "mcaCapacityAssessment.canUnderstandInformationReasoningRequired",
      ),
      multiline: true,
      rows: "5",
    })

    .withFieldAlwaysVisible({
      fieldName: "canRetainInformation",
      type: "radio",
      ...new FieldBuilder().withSchema(yesNoSchema).captureValueLabel().build(),
      label: tk("mcaCapacityAssessment.canRetainInformation"),
      requiredLabel: tk("mcaCapacityAssessment.canRetainInformationRequired"),
      options: [
        { label: tk("common.yes"), value: "yes" },
        { label: tk("common.no"), value: "no" },
      ],
    })

    .withReadonlyFieldAlwaysVisible({
      fieldName: "canRetainInformationBanner",
      type: "banner",
      bannerType: "info",
      body: tk("mcaCapacityAssessment.canRetainInformationBanner"),
    })

    .withFieldAlwaysVisible({
      fieldName: "canRetainInformationReasoning",
      type: "textbox",
      ...new FieldBuilder().withSchema(longTextSchema).build(),
      label: tk("mcaCapacityAssessment.canRetainInformationReasoning"),
      requiredLabel: tk(
        "mcaCapacityAssessment.canRetainInformationReasoningRequired",
      ),
      multiline: true,
      rows: "5",
    })

    // 3a. Weigh information
    .withFieldAlwaysVisible({
      fieldName: "canWeighInformation",
      type: "radio",
      ...new FieldBuilder().withSchema(yesNoSchema).captureValueLabel().build(),
      label: tk("mcaCapacityAssessment.canWeighInformation"),
      requiredLabel: tk("mcaCapacityAssessment.canWeighInformationRequired"),
      options: [
        { label: tk("common.yes"), value: "yes" },
        { label: tk("common.no"), value: "no" },
      ],
    })

    .withReadonlyFieldAlwaysVisible({
      fieldName: "canWeighInformationBanner",
      type: "banner",
      bannerType: "info",
      body: tk("mcaCapacityAssessment.canWeighInformationBanner"),
    })

    .withFieldAlwaysVisible({
      fieldName: "canWeighInformationReasoning",
      type: "textbox",
      ...new FieldBuilder().withSchema(longTextSchema).build(),
      label: tk("mcaCapacityAssessment.canWeighInformationReasoning"),
      requiredLabel: tk(
        "mcaCapacityAssessment.canWeighInformationReasoningRequired",
      ),
      multiline: true,
      rows: "5",
    })

    // 3b. Use information
    .withFieldAlwaysVisible({
      fieldName: "canUseInformation",
      type: "radio",
      ...new FieldBuilder().withSchema(yesNoSchema).captureValueLabel().build(),
      label: tk("mcaCapacityAssessment.canUseInformation"),
      requiredLabel: tk("mcaCapacityAssessment.canUseInformationRequired"),
      options: [
        { label: tk("common.yes"), value: "yes" },
        { label: tk("common.no"), value: "no" },
      ],
    })

    .withReadonlyFieldAlwaysVisible({
      fieldName: "canUseInformationBanner",
      type: "banner",
      bannerType: "info",
      body: tk("mcaCapacityAssessment.canUseInformationBanner"),
    })

    .withFieldAlwaysVisible({
      fieldName: "canUseInformationReasoning",
      type: "textbox",
      ...new FieldBuilder().withSchema(longTextSchema).build(),
      label: tk("mcaCapacityAssessment.canUseInformationReasoning"),
      requiredLabel: tk(
        "mcaCapacityAssessment.canUseInformationReasoningRequired",
      ),
      multiline: true,
      rows: "5",
    })

    // Communicate decision
    .withFieldAlwaysVisible({
      fieldName: "canCommunicateDecision",
      type: "radio",
      ...new FieldBuilder().withSchema(yesNoSchema).captureValueLabel().build(),
      label: tk("mcaCapacityAssessment.canCommunicateDecision"),
      requiredLabel: tk("mcaCapacityAssessment.canCommunicateDecisionRequired"),
      options: [
        { label: tk("common.yes"), value: "yes" },
        { label: tk("common.no"), value: "no" },
      ],
    })

    .withReadonlyFieldAlwaysVisible({
      fieldName: "canCommunicateDecisionBanner",
      type: "banner",
      bannerType: "info",
      body: tk("mcaCapacityAssessment.canCommunicateDecisionBanner"),
    })

    .withFieldAlwaysVisible({
      fieldName: "canCommunicateDecisionReasoning",
      type: "textbox",
      ...new FieldBuilder().withSchema(longTextSchema).build(),
      label: tk("mcaCapacityAssessment.canCommunicateDecisionReasoning"),
      requiredLabel: tk(
        "mcaCapacityAssessment.canCommunicateDecisionReasoningRequired",
      ),
      multiline: true,
      rows: "5",
    })

    // Impairment or disturbance
    .withReadonlyFieldAlwaysVisible({
      fieldName: "impairmentMentalCapacityTable",
      type: "mental-capacity-table",
    })

    // Exit-2: Allow early exit if person can function normally (all five tests answered "yes")
    .withFieldConditionallyVisible({
      fieldName: "earlyExitCapable",
      type: "radio",
      ...new FieldBuilder()
        .withSchema(z.enum(["end", "continue"]))
        .captureValueLabel()
        .build(),
      label: tk("mcaCapacityAssessment.earlyExitCapable.body"),
      requiredLabel: tk("mcaCapacityAssessment.earlyExitCapable.required"),
      options: [
        {
          label: tk("mcaCapacityAssessment.earlyExitCapable.end"),
          value: "end",
        },
        {
          label: tk("mcaCapacityAssessment.earlyExitCapable.continue"),
          value: "continue",
        },
      ],
      isVisible: ({ formValues }) =>
        [
          formValues.canUnderstandInformation?.value,
          formValues.canRetainInformation?.value,
          formValues.canWeighInformation?.value,
          formValues.canUseInformation?.value,
          formValues.canCommunicateDecision?.value,
        ].every((answer) => answer === "yes"),
    })

    .withReadonlyFieldConditionallyVisible({
      fieldName: "impairmentDisturbanceInfoBanner",
      type: "banner",
      bannerType: "info",
      body: tk("mcaCapacityAssessment.impairmentDisturbanceInfoBannerBody"),
      isVisible: ({ formValues }) =>
        match(formValues.earlyExitCapable?.value)
          .with("continue", () => true)
          .with("end", () => false)
          .with(
            undefined,
            () =>
              ![
                formValues.canUnderstandInformation?.value,
                formValues.canRetainInformation?.value,
                formValues.canWeighInformation?.value,
                formValues.canUseInformation?.value,
                formValues.canCommunicateDecision?.value,
              ].every((answer) => answer === "yes"),
          )
          .exhaustive(),
    })

    .withFieldConditionallyVisible({
      fieldName: "impairmentDisturbance",
      type: "radio",
      ...new FieldBuilder().withSchema(yesNoSchema).captureValueLabel().build(),
      label: tk("mcaCapacityAssessment.impairmentDisturbance"),
      requiredLabel: tk("mcaCapacityAssessment.impairmentDisturbanceRequired"),
      options: [
        { label: tk("common.yes"), value: "yes" },
        { label: tk("common.no"), value: "no" },
      ],
      isVisible: ({ formValues }) =>
        match(formValues.earlyExitCapable?.value)
          .with("continue", () => true)
          .with("end", () => false)
          .with(
            undefined,
            () =>
              ![
                formValues.canUnderstandInformation?.value,
                formValues.canRetainInformation?.value,
                formValues.canWeighInformation?.value,
                formValues.canUseInformation?.value,
                formValues.canCommunicateDecision?.value,
              ].every((answer) => answer === "yes"),
          )
          .exhaustive(),
    })

    // Exit-3: Allow early exit if functional limitations are not due to impairment in the mind or brain
    .withFieldConditionallyVisible({
      fieldName: "earlyExitNoImpairment",
      type: "radio",
      ...new FieldBuilder()
        .withSchema(z.enum(["end", "continue"]))
        .captureValueLabel()
        .build(),
      label: tk("mcaCapacityAssessment.earlyExitNoImpairment.body"),
      requiredLabel: tk("mcaCapacityAssessment.earlyExitNoImpairment.required"),
      options: [
        {
          label: tk("mcaCapacityAssessment.earlyExitNoImpairment.end"),
          value: "end",
        },
        {
          label: tk("mcaCapacityAssessment.earlyExitNoImpairment.continue"),
          value: "continue",
        },
      ],
      isVisible: ({ formValues }) =>
        formValues.impairmentDisturbance?.value === "no" &&
        formValues.earlyExitCapable?.value !== "end",
    })

    .withFieldConditionallyVisible({
      fieldName: "impairmentType",
      type: "textbox",
      ...new FieldBuilder().withSchema(longTextSchema).build(),
      label: tk("mcaCapacityAssessment.impairmentType.title"),
      requiredLabel: tk("mcaCapacityAssessment.impairmentType.required"),
      isVisible: ({ formValues }) =>
        formValues.impairmentDisturbance?.value === "yes" &&
        formValues.earlyExitCapable?.value !== "end",
      multiline: true,
      rows: "5",
    })
    .withReadonlyFieldConditionallyVisible({
      fieldName: "impairmentTypeBanner",
      type: "banner",
      bannerType: "info",
      body: tk("mcaCapacityAssessment.impairmentType.banner"),
      isVisible: ({ formValues }) =>
        formValues.impairmentDisturbance?.value === "yes" &&
        formValues.earlyExitCapable?.value !== "end",
    })

    .withReadonlyFieldConditionallyVisible({
      fieldName: "lackOfCapacityWarningBanner",
      type: "banner",
      bannerType: "warning",
      body: tk("mcaCapacityAssessment.lackOfCapacityWarningBanner"),
      isVisible: ({ formValues }) =>
        formValues.earlyExitCapable?.value !== "end" &&
        formValues.earlyExitNoImpairment?.value !== "end",
    })

    // Lack of capacity
    .withReadonlyFieldConditionallyVisible({
      fieldName: "lackOfCapacityMentalCapacityTable",
      type: "mental-capacity-table",
      isVisible: ({ formValues }) =>
        formValues.earlyExitCapable?.value !== "end" &&
        formValues.earlyExitNoImpairment?.value !== "end",
    })

    .withFieldConditionallyVisible({
      fieldName: "lackOfCapacity",
      type: "radio",
      ...new FieldBuilder().withSchema(yesNoSchema).captureValueLabel().build(),
      label: tk("mcaCapacityAssessment.lackOfCapacity"),
      requiredLabel: tk("mcaCapacityAssessment.lackOfCapacityRequired"),
      options: [
        { label: tk("common.yes"), value: "yes" },
        { label: tk("common.no"), value: "no" },
      ],
      isVisible: ({ formValues }) =>
        formValues.earlyExitCapable?.value !== "end" &&
        formValues.earlyExitNoImpairment?.value !== "end",
    })

    // Exit-1: Allow early exit if person is not found to lack capacity
    .withFieldConditionallyVisible({
      fieldName: "earlyExitNoLackOfCapacity",
      type: "radio",
      ...new FieldBuilder()
        .withSchema(z.enum(["end", "continue"]))
        .captureValueLabel()
        .build(),
      label: tk("mcaCapacityAssessment.earlyExitNoLackOfCapacity.body"),
      requiredLabel: tk(
        "mcaCapacityAssessment.earlyExitNoLackOfCapacity.required",
      ),
      options: [
        {
          label: tk("mcaCapacityAssessment.earlyExitNoLackOfCapacity.end"),
          value: "end",
        },
        {
          label: tk("mcaCapacityAssessment.earlyExitNoLackOfCapacity.continue"),
          value: "continue",
        },
      ],
      isVisible: ({ formValues }) =>
        formValues.lackOfCapacity?.value === "no" &&
        formValues.earlyExitCapable?.value !== "end" &&
        formValues.earlyExitNoImpairment?.value !== "end",
    })

    .withFieldConditionallyVisible({
      fieldName: "reasoningLackOfCapacity",
      type: "textbox",
      ...new FieldBuilder().withSchema(longTextSchema).build(),
      label: tk("mcaCapacityAssessment.reasoningLackOfCapacity.title"),
      requiredLabel: tk(
        "mcaCapacityAssessment.reasoningLackOfCapacity.required",
      ),
      multiline: true,
      rows: "5",
      isVisible: ({ formValues }) =>
        (formValues.lackOfCapacity?.value === "yes" ||
          formValues.earlyExitNoLackOfCapacity?.value === "continue") &&
        formValues.earlyExitCapable?.value !== "end" &&
        formValues.earlyExitNoImpairment?.value !== "end",
    })

    .withReadonlyFieldConditionallyVisible({
      fieldName: "reasoningLackOfCapacityInfoBanner",
      type: "banner",
      bannerType: "info",
      body: tk("mcaCapacityAssessment.reasoningLackOfCapacity.banner"),
      isVisible: ({ formValues }) =>
        (formValues.lackOfCapacity?.value === "yes" ||
          formValues.earlyExitNoLackOfCapacity?.value === "continue") &&
        formValues.earlyExitCapable?.value !== "end" &&
        formValues.earlyExitNoImpairment?.value !== "end",
    })

    .withFieldConditionallyVisible({
      fieldName: "bestInterestSteps",
      type: "textbox",
      ...new FieldBuilder().withSchema(longTextSchema).build(),
      label: tk("mcaCapacityAssessment.bestInterestSteps"),
      requiredLabel: tk("mcaCapacityAssessment.bestInterestStepsRequired"),
      multiline: true,
      rows: "5",
      isVisible: ({ formValues }) =>
        (formValues.lackOfCapacity?.value === "yes" ||
          formValues.earlyExitNoLackOfCapacity?.value === "continue") &&
        formValues.earlyExitCapable?.value !== "end" &&
        formValues.earlyExitNoImpairment?.value !== "end",
    })

    .withReadonlyFieldConditionallyVisible({
      fieldName: "bestInterestStepsInfoBanner",
      type: "banner",
      bannerType: "info",
      body: tk("mcaCapacityAssessment.bestInterestStepsInfoBanner"),
      isVisible: ({ formValues }) =>
        (formValues.lackOfCapacity?.value === "yes" ||
          formValues.earlyExitNoLackOfCapacity?.value === "continue") &&
        formValues.earlyExitCapable?.value !== "end" &&
        formValues.earlyExitNoImpairment?.value !== "end",
    })

    .withFieldConditionallyVisible({
      fieldName: "bestInterestPhysicalHealthCondition",
      type: "radio",
      ...new FieldBuilder().withSchema(yesNoSchema).captureValueLabel().build(),
      label: tk("mcaCapacityAssessment.bestInterestPhysicalHealthCondition"),
      requiredLabel: tk(
        "mcaCapacityAssessment.bestInterestPhysicalHealthConditionRequired",
      ),
      options: [
        { label: tk("common.yes"), value: "yes" },
        { label: tk("common.no"), value: "no" },
      ],
      isVisible: ({ formValues }) =>
        (formValues.lackOfCapacity?.value === "yes" ||
          formValues.earlyExitNoLackOfCapacity?.value === "continue") &&
        formValues.earlyExitCapable?.value !== "end" &&
        formValues.earlyExitNoImpairment?.value !== "end",
    })

    .withFieldConditionallyVisible({
      fieldName: "bestInterestPhysicalConditionDescription",
      type: "textbox",
      ...new FieldBuilder().withSchema(longTextSchema).build(),
      label: tk(
        "mcaCapacityAssessment.bestInterestPhysicalConditionDescription",
      ),
      requiredLabel: tk(
        "mcaCapacityAssessment.bestInterestPhysicalConditionDescriptionRequired",
      ),
      multiline: true,
      rows: "5",
      isVisible: ({ formValues }) =>
        formValues.bestInterestPhysicalHealthCondition?.value === "yes",
    })

    .withFieldConditionallyVisible({
      fieldName: "bestInterestExceptionalCircumstances",
      type: "textbox",
      ...new FieldBuilder().withSchema(longTextSchema).build(),
      label: tk("mcaCapacityAssessment.bestInterestExceptionalCircumstances"),
      requiredLabel: tk(
        "mcaCapacityAssessment.bestInterestExceptionalCircumstancesRequired",
      ),
      multiline: true,
      rows: "5",
      isVisible: ({ formValues }) =>
        formValues.bestInterestPhysicalHealthCondition?.value === "no",
    })

    .withFieldConditionallyVisible({
      fieldName: "bestInterestAct",
      type: "textbox",
      ...new FieldBuilder().withSchema(longTextSchema).build(),
      label: tk("mcaCapacityAssessment.bestInterestAct"),
      requiredLabel: tk("mcaCapacityAssessment.bestInterestActRequired"),
      multiline: true,
      rows: "5",
      isVisible: ({ formValues }) =>
        (formValues.lackOfCapacity?.value === "yes" ||
          formValues.earlyExitNoLackOfCapacity?.value === "continue") &&
        formValues.earlyExitCapable?.value !== "end" &&
        formValues.earlyExitNoImpairment?.value !== "end",
    })

    .withFieldConditionallyVisible({
      fieldName: "restraintNecessary",
      type: "radio",
      ...new FieldBuilder()
        .withSchema(z.enum(["yes", "noRestraintUsed"]))
        .captureValueLabel()
        .build(),
      label: tk("mcaCapacityAssessment.restraintNecessary"),
      requiredLabel: tk("mcaCapacityAssessment.restraintNecessaryRequired"),
      options: [
        { label: tk("common.yes"), value: "yes" },
        {
          label: tk("mcaCapacityAssessment.noRestraintUsed"),
          value: "noRestraintUsed",
        },
      ],
      isVisible: ({ formValues }) =>
        (formValues.lackOfCapacity?.value === "yes" ||
          formValues.earlyExitNoLackOfCapacity?.value === "continue") &&
        formValues.earlyExitCapable?.value !== "end" &&
        formValues.earlyExitNoImpairment?.value !== "end",
    })

    .withReadonlyFieldConditionallyVisible({
      fieldName: "restraintRiskInfoBanner",
      type: "banner",
      bannerType: "info",
      body: tk("mcaCapacityAssessment.restraintRiskInfoBanner"),
      isVisible: ({ formValues }) =>
        (formValues.lackOfCapacity?.value === "yes" ||
          formValues.earlyExitNoLackOfCapacity?.value === "continue") &&
        formValues.earlyExitCapable?.value !== "end" &&
        formValues.earlyExitNoImpairment?.value !== "end",
    })

    .withFieldConditionallyVisible({
      fieldName: "restraintRiskReasoning",
      type: "textbox",
      ...new FieldBuilder().withSchema(longTextSchema).build(),
      label: tk("mcaCapacityAssessment.restraintRiskReasoning"),
      requiredLabel: tk("mcaCapacityAssessment.restraintRiskReasoningRequired"),
      multiline: true,
      rows: "5",
      isVisible: ({ formValues }) =>
        (formValues.lackOfCapacity?.value === "yes" ||
          formValues.earlyExitNoLackOfCapacity?.value === "continue") &&
        formValues.earlyExitCapable?.value !== "end" &&
        formValues.earlyExitNoImpairment?.value !== "end",
    })

    .withFieldConditionallyVisible({
      fieldName: "useOfForceType",
      type: "multi-select",
      schema: z
        .array(valueAndLabelSchemaDeprecated(z.string()))
        .min(1, "At least one option must be selected."),
      isVisible: ({ formValues }) =>
        formValues.restraintNecessary?.value === "yes",
      label: tk("mcaCapacityAssessment.useOfForceType.title"),
      requiredLabel: tk("mcaCapacityAssessment.useOfForceType.required"),
      options: args?.useOfForceTypeOptions ?? defaultUseOfForceTypeOptions,
    })

    .withPage(["introduction"])
    .withPage([
      "assessingIndividual",
      "assessingIndividualDescriptionBanner",
      "healthServiceAssessorDescriptionBanner",
      "ambulanceStaffName",
      "otherAssessor",
    ])
    .withPage(["decisionUnableToMake", "decisionUnableToMakeBanner"])
    .withPage([
      "canUnderstandInformation",
      "canUnderstandInformationBanner",
      "canUnderstandInformationReasoning",
    ])
    .withPage([
      "canRetainInformation",
      "canRetainInformationBanner",
      "canRetainInformationReasoning",
    ])
    .withPage([
      "canWeighInformation",
      "canWeighInformationBanner",
      "canWeighInformationReasoning",
      "canUseInformation",
      "canUseInformationBanner",
      "canUseInformationReasoning",
    ])
    .withPage([
      "canCommunicateDecision",
      "canCommunicateDecisionBanner",
      "canCommunicateDecisionReasoning",
    ])
    .withPage([
      "impairmentMentalCapacityTable",
      "earlyExitCapable",
      "impairmentDisturbanceInfoBanner",
      "impairmentDisturbance",
      "earlyExitNoImpairment",
    ])
    .withPage(["impairmentType", "impairmentTypeBanner"])
    .withPage([
      "lackOfCapacityMentalCapacityTable",
      "lackOfCapacityWarningBanner",
      "lackOfCapacity",
      "earlyExitNoLackOfCapacity",
    ])
    .withPage(["reasoningLackOfCapacity", "reasoningLackOfCapacityInfoBanner"])
    .withPage(["bestInterestSteps", "bestInterestStepsInfoBanner"])
    .withPage([
      "bestInterestPhysicalHealthCondition",
      "bestInterestPhysicalConditionDescription",
      "bestInterestExceptionalCircumstances",
    ])
    .withPage(["bestInterestAct"])
    .withPage([
      "restraintNecessary",
      "restraintRiskInfoBanner",
      "restraintRiskReasoning",
    ])
    .withPage(["useOfForceType"])
    .build({ t });

export const formSpecification = buildMcaCapacityAssessmentFormSpecification();

export type McaCapacityAssessmentFormData = ReturnType<
  typeof formSpecification
>["formType"];
